//#region Keyboard handlers
import { isUndefined } from 'lodash-es';
import type { KeyboardEvent, RefObject, WheelEvent } from 'react';

import type { RelativeAbsolutePosition } from '../../../types/pointer-events.js';
import { KeyboardControls } from '../constants.js';
import type {
  InteractionsHandlers,
  InteractionsOverlayProps,
} from '../types.js';
import { reset } from './reset.js';

type KeyboardInteractions = Pick<
  InteractionsOverlayProps,
  'selectionArea' | 'onSelectEnd' | 'onModeChange'
> & {
  lastMousePositionRef: RefObject<RelativeAbsolutePosition | undefined>;
  panDeltaRef: RefObject<number | undefined>;
  eventCancelled: RefObject<boolean>;
  interaction: InteractionsHandlers;
  event: KeyboardEvent;
};

const isTemporaryZoomMode = (event: WheelEvent | KeyboardEvent) =>
  event.metaKey || event.ctrlKey;

export const handleKeyDown = ({
  event,
  selectionArea,
  eventCancelled,
  onSelectEnd,
  onModeChange,
  interaction,
  panDeltaRef,
  lastMousePositionRef,
}: KeyboardInteractions) => {
  switch (event.key) {
    case KeyboardControls.KEYBOARD_EXPLORE_MODE_SHORTCUT:
    case KeyboardControls.KEYBOARD_EXPLORE_MODE_SHORTCUT_ALTERNATIVE: {
      reset(lastMousePositionRef, panDeltaRef);

      if (!isUndefined(selectionArea)) {
        eventCancelled.current = true;
        onSelectEnd?.({ cancelled: true });
      }

      onModeChange?.({ mode: 'explore', keyboard: true });
      break;
    }
    case KeyboardControls.KEYBOARD_PAN_MODE_SHORTCUT:
    case KeyboardControls.KEYBOARD_PAN_MODE_SHORTCUT_ALTERNATIVE: {
      reset(lastMousePositionRef, panDeltaRef);

      onModeChange?.({ mode: 'pan', keyboard: true });
      break;
    }
    case KeyboardControls.KEYBOARD_ZOOM_MODE_SHORTCUT:
    case KeyboardControls.KEYBOARD_ZOOM_MODE_SHORTCUT_ALTERNATIVE: {
      reset(lastMousePositionRef, panDeltaRef);

      if (!isUndefined(selectionArea)) {
        eventCancelled.current = true;
        onSelectEnd?.({ cancelled: true });
      }

      onModeChange?.({ mode: 'zoom', keyboard: true });
      break;
    }
    case KeyboardControls.KEYBOARD_RESET_SHORTCUT:
    case KeyboardControls.KEYBOARD_RESET_SHORTCUT_ALTERNATIVE:
      interaction.onDoubleClickOrTap('keyboard');
      break;
    case KeyboardControls.UP_ARROW: {
      event.preventDefault();
      interaction.onKeyUp(isTemporaryZoomMode(event));

      break;
    }
    case KeyboardControls.DOWN_ARROW: {
      event.preventDefault();
      interaction.onKeyDown(isTemporaryZoomMode(event));

      break;
    }
    case KeyboardControls.LEFT_ARROW: {
      interaction.onKeyLeft(event.shiftKey);

      break;
    }
    case KeyboardControls.RIGHT_ARROW: {
      interaction.onKeyRight(event.shiftKey);

      break;
    }
    case KeyboardControls.ENTER: {
      if (!isUndefined(selectionArea)) {
        interaction.onKeyEnter();
      }
      break;
    }
    case KeyboardControls.ESCAPE: {
      interaction.onKeyEscape();
      reset(lastMousePositionRef, panDeltaRef);

      break;
    }
  }
};
//#endregion
